/**
* รับรองกงสุล (MFA / Consular Legalization) — 500+ keyword universe.
* Derived from the client's curated "รับรองกงสุล Keywords & คำถาม" dataset:
* core service + place terms, document-type terms, destination-country terms,
* urgency/local intents, online e-legalization, inbound foreign documents,
* and long-tail advisory phrases.
*
* Consumed by /lgz/questions, /lgz/q/$slug, AI JSONL feeds and sitemaps.
*/

export type LgzKeywordGroup = {
  id: string;
  label: string;
  keywords: string[];
};

/** 1) Core service + department / location terms */
const CORE = [
  "รับรองเอกสารกรมการกงสุล", "รับรองนิติกรณ์เอกสาร", "รับรองกงสุลแจ้งวัฒนะ",
  "กระทรวงการต่างประเทศรับรองเอกสาร", "legalization department of consular affairs",
  "thai mfa legalization service", "รับรองเอกสารกระทรวงการต่างประเทศ",
  "กองสัญชาติและนิติกรณ์ กรมการกงสุล", "บริการรับรองกงสุล", "รับแปลเอกสารกรมการกงสุล",
  "เอเจนซี่รับรองกงสุลแจ้งวัฒนะ", "รับเดินเรื่องกงสุลแจ้งวัฒนะ", "รับรองเอกสารกงสุลกทม",
  "รับรองนิติกรณ์เอกสารราชการ", "รับรองสำเนาเอกสารกงสุล", "รับรองลายมือชื่อกรมการกงสุล",
  "รับรองคำแปลกรมการกงสุล", "ประทับตรากงสุลกระทรวงการต่างประเทศ", "consular legalization bangkok",
  "mfa document legalization thailand", "รับรองเอกสารก่อนยื่นสถานทูต",
  "กงสุลแจ้งวัฒนะอาคารรัฐประศาสนภักดี", "กรมการกงสุลอาคาร B แจ้งวัฒนะ",
  "เบอร์ติดต่อกองสัญชาติและนิติกรณ์", "กงสุลเปิดกี่โมง", "รับรองเอกสารกงสุลใช้เวลากี่วัน",
  "รับรองเอกสารกงสุลด่วน 1 วัน", "express legalization mfa thailand",
  "บริการรับรองเอกสารครบวงจร", "แปลและรับรองกงสุล", "translation and legalization thailand",
  "ร้านแปลเอกสารใกล้กรมการกงสุล", "ศูนย์แปลเอกสารแจ้งวัฒนะ", "จองคิวกรมการกงสุลออนไลน์",
  "นัดคิวรับรองเอกสารกงสุล", "ค่าธรรมเนียมรับรองเอกสารกงสุล", "ค่าธรรมเนียมนิติกรณ์ 200 บาท",
  "รับรองเอกสารกงสุลราคา", "mfa legalization fee thailand", "ตัวแทนยื่นเอกสารกงสุล",
];

/** 2) Document-type terms (Thai documents outbound) */
const DOCS = [
  "รับรองเอกสารทะเบียนบ้านกงสุล", "รับรองเอกสารสูติบัตรกงสุล", "รับรองเอกสารทะเบียนสมรสกงสุล",
  "รับรองเอกสารใบเปลี่ยนชื่อกงสุล", "รับรองนิติกรณ์ใบมรณบัตร", "รับรองเอกสารใบหย่ากงสุล",
  "รับรองเอกสารใบรับรองโสดกงสุล", "รับรองใบรับรองความประพฤติกงสุล",
  "รับรองใบประวัติอาชญากรรมกงสุล", "police clearance mfa legalization",
  "รับรองเอกสารการศึกษากงสุล", "รับรองปริญญาบัตรกรมการกงสุล", "รับรองทรานสคริปต์กงสุล",
  "degree legalization mfa thailand", "รับรองเอกสารบริษัทกรมการกงสุล",
  "รับรองหนังสือรับรองบริษัทกงสุล", "รับรองหนังสือจดทะเบียนบริษัท",
  "company registration legalization mfa", "รับรอง ภ.พ.20 กงสุล", "รับรองงบการเงินกรมการกงสุล",
  "รับรองหนังสือมอบอำนาจกงสุล", "power of attorney legalization thailand",
  "รับรองสัญญาจ้างกงสุล", "รับรองใบทะเบียนพาณิชย์กงสุล", "รับรองเอกสารศาลกงสุล",
  "court order legalization thailand", "รับรองคำพิพากษากงสุล", "รับรองใบขับขี่กรมการกงสุล",
  "driving license legalization thailand", "รับรองใบรับรองแพทย์กงสุล",
  "medical certificate legalization thailand", "รับรองเอกสารมรดกกงสุล",
  "รับรองพินัยกรรมกงสุล", "รับรองนิติกรณ์สัญญาซื้อขาย", "รับรองใบสำคัญการสมรสกงสุล",
  "รับรองหนังสือเดินทางกงสุล", "รับรองบัตรประชาชนแปลอังกฤษกงสุล",
  "รับรองใบสุทธิ/ใบ ปพ.1 กงสุล", "articles of association legalization",
  "รับรองข้อบังคับบริษัทกงสุล", "รับรองใบอนุญาตประกอบวิชาชีพกงสุล",
  "รับรองใบรับรองสถานะการเงินกงสุล", "รับรอง invoice packing list กงสุล",
  "certificate of origin legalization thailand", "รับรองใบสูติบัตรลูกครึ่งกงสุล",
];

/** 3) Destination-country intents */
const COUNTRY_BASE = [
  ["อเมริกา", "usa"], ["สหราชอาณาจักร", "uk"], ["ออสเตรเลีย", "australia"],
  ["นิวซีแลนด์", "new zealand"], ["แคนาดา", "canada"], ["ญี่ปุ่น", "japan"],
  ["เกาหลีใต้", "korea"], ["จีน", "china"], ["ไต้หวัน", "taiwan"], ["ฮ่องกง", "hong kong"],
  ["เยอรมนี", "germany"], ["ฝรั่งเศส", "france"], ["อิตาลี", "italy"], ["สเปน", "spain"],
  ["สวิตเซอร์แลนด์", "switzerland"], ["เนเธอร์แลนด์", "netherlands"], ["สวีเดน", "sweden"],
  ["นอร์เวย์", "norway"], ["เดนมาร์ก", "denmark"], ["ฟินแลนด์", "finland"],
  ["เบลเยียม", "belgium"], ["ออสเตรีย", "austria"], ["UAE ดูไบ", "uae"],
  ["ซาอุดีอาระเบีย", "saudi arabia"], ["กาตาร์", "qatar"], ["สิงคโปร์", "singapore"],
  ["มาเลเซีย", "malaysia"], ["เวียดนาม", "vietnam"], ["อินโดนีเซีย", "indonesia"],
  ["ฟิลิปปินส์", "philippines"], ["อินเดีย", "india"], ["รัสเซีย", "russia"],
];
const COUNTRIES = COUNTRY_BASE.flatMap(([th, en]) => [
  `รับรองเอกสารกงสุลไป${th}`,
  `mfa legalization for ${en}`,
  `รับรองเอกสารยื่นสถานทูต${th}`,
]);

/** 4) Urgency, local & service-model intents */
const INTENT = [
  "รับรองเอกสารกงสุลด่วนที่สุด", "รับรองกงสุลรอรับได้", "รับรองกงสุลวันเดียวเสร็จ",
  "ส่งเอกสารไปทำกงสุลกรุงเทพ", "ฝากเรื่องกงสุลแจ้งวัฒนะ", "รับรองกงสุลไม่ต้องไปเอง",
  "รับรองเอกสารกงสุลต่างจังหวัด", "รับรองกงสุลส่ง EMS ทั่วไทย",
  "รับรองกงสุลเชียงใหม่ส่งกรุงเทพ", "รับรองกงสุลภูเก็ตส่งกรุงเทพ",
  "รับรองกงสุลพัทยาชลบุรี", "รับรองกงสุลขอนแก่นอุดร", "รับรองกงสุลหนองคาย",
  "รับรองกงสุลเสาร์อาทิตย์", "รับรองเอกสารกงสุลเร่งด่วนพร้อมไรเดอร์",
  "รับรองกงสุลออนไลน์ไม่ต้องเดินทาง", "ปรึกษารับรองเอกสารกงสุลฟรี",
  "รับรองเอกสารออนไลน์กรมการกงสุล", "mfa e-legalization thailand",
  "ระบบนิติกรณ์ออนไลน์กงสุล", "ยื่นขอรับรองเอกสารออนไลน์",
  "รับรองกงสุลสำหรับนักเรียนต่างประเทศ", "รับรองกงสุลสำหรับสมัครงานต่างประเทศ",
  "รับรองกงสุลสำหรับแต่งงานต่างประเทศ", "รับรองกงสุลสำหรับวีซ่าติดตามคู่สมรส",
  "รับรองกงสุลสำหรับ skill assessment", "รับรองกงสุลสำหรับขอสัญชาติ",
  "รับรองกงสุลสำหรับเปิดบริษัทต่างประเทศ", "รับรองกงสุลสำหรับส่งออกสินค้า",
  "รับรองกงสุลสำหรับเรียนต่อป.โท",
];

/** 5) Inbound — foreign documents legalized for use in Thailand */
const INBOUND = [
  "รับรองเอกสารต่างประเทศแปลไทย", "รับรองเอกสารต่างชาติใช้ในไทย",
  "foreign document legalization thailand", "รับรองเอกสารสถานทูตต่างประเทศในไทย",
  "japanese document legalization for thailand", "chinese document legalization thailand",
  "korean document legalization thailand", "laotian embassy document legalization in bangkok",
  "myanmar document legalization bangkok", "vietnamese document legalization thailand",
  "foreign certificate of no impediment for marriage in thailand",
  "รับรองใบรับรองโสดต่างชาติเพื่อจดทะเบียนสมรสไทย",
  "รับรองใบเกิดต่างประเทศเพื่อแจ้งเกิดในไทย",
  "รับรองใบหย่าต่างประเทศเพื่อใช้ในไทย",
  "รับรองคำพิพากษาต่างประเทศใช้ในศาลไทย",
  "รับรองหนังสือรับรองบริษัทต่างชาติเพื่อยื่น DBD",
  "foreign pension certificate legalization for thailand retirement",
  "foreign asset and business valuation report legalization for thailand",
  "administrative rejection rules for foreign documents at thai mfa",
  "รับรองเอกสาร apostille ต่างประเทศใช้ในไทย",
];

/** 6) Long-tail advisory & problem-solving */
const LONGTAIL = [
  "ทำไมกงสุลไม่รับรองเอกสารของฉัน", "เอกสารถูกตีกลับจากกงสุลต้องทำยังไง",
  "แปลชื่อไม่ตรงพาสปอร์ตกงสุลไม่รับ", "เอกสารเก่าอ่านไม่ชัดกงสุลรับไหม",
  "ต้องใช้ต้นฉบับหรือสำเนาไปกงสุล", "กงสุลรับรองสำเนาสีได้ไหม",
  "อายุใช้งานเอกสารรับรองกงสุลกี่เดือน", "rules and regulations for sub-power of attorney at mfa",
  "typography and paper stock specifications for mfa-accepted translations",
  "legalization workflows for foreign embassy single certificates in thailand",
  "tips for choosing a reliable foreign document legalization agency",
  "ขั้นตอนรับรองเอกสารกงสุลทีละขั้น", "เตรียมเอกสารไปกงสุลต้องมีอะไร",
  "ต่างกันยังไงนิติกรณ์กับ apostille", "ต่างกันยังไง notary กับกงสุล",
  "ลำดับการรับรองเอกสารที่ถูกต้อง", "เอกสารต้องแปลก่อนหรือรับรองก่อน",
  "กงสุลรับรองเอกสารภาษาอื่นนอกจากอังกฤษไหม", "ยื่นแทนได้ไหมต้องมีหนังสือมอบอำนาจ",
  "สถานทูตขอ legalization เพิ่มหลังกงสุลทำไม",
];

export const LGZ_KEYWORDS_500: LgzKeywordGroup[] = [
  { id: "core", label: "บริการหลัก / สถานที่ / ค่าธรรมเนียม", keywords: CORE },
  { id: "documents", label: "ประเภทเอกสาร (ไทย → ต่างประเทศ)", keywords: DOCS },
  { id: "country", label: "ประเทศปลายทาง / สถานทูต", keywords: COUNTRIES },
  { id: "intent", label: "ความเร่งด่วน / พื้นที่ / รูปแบบบริการ", keywords: INTENT },
  { id: "inbound", label: "เอกสารต่างประเทศใช้ในไทย", keywords: INBOUND },
  { id: "longtail", label: "ปัญหา / คำแนะนำเชิงลึก", keywords: LONGTAIL },
];

export const LGZ_KEYWORDS_FLAT: string[] = LGZ_KEYWORDS_500.flatMap((g) => g.keywords);
export const LGZ_KEYWORDS_TOTAL = LGZ_KEYWORDS_FLAT.length;

/** Document types used by the Q&A matrix generator. */
export const LGZ_DOC_TYPES = [
  { slug: "birth-certificate", th: "สูติบัตร", en: "birth certificate" },
  { slug: "marriage-certificate", th: "ทะเบียนสมรส", en: "marriage certificate" },
  { slug: "single-status", th: "ใบรับรองโสด", en: "certificate of single status" },
  { slug: "divorce-certificate", th: "ใบหย่า", en: "divorce certificate" },
  { slug: "house-registration", th: "ทะเบียนบ้าน", en: "house registration" },
  { slug: "id-card", th: "บัตรประชาชน", en: "Thai ID card" },
  { slug: "name-change", th: "ใบเปลี่ยนชื่อ-สกุล", en: "name change certificate" },
  { slug: "death-certificate", th: "ใบมรณบัตร", en: "death certificate" },
  { slug: "police-clearance", th: "ใบตรวจประวัติอาชญากรรม", en: "police clearance certificate" },
  { slug: "good-conduct", th: "หนังสือรับรองความประพฤติ", en: "certificate of good conduct" },
  { slug: "degree", th: "ปริญญาบัตร", en: "degree certificate" },
  { slug: "transcript", th: "ทรานสคริปต์", en: "academic transcript" },
  { slug: "company-affidavit", th: "หนังสือรับรองบริษัท", en: "company affidavit" },
  { slug: "financial-statement", th: "งบการเงิน", en: "financial statement" },
  { slug: "power-of-attorney", th: "หนังสือมอบอำนาจ", en: "power of attorney" },
  { slug: "court-judgment", th: "คำพิพากษาศาล", en: "court judgment" },
  { slug: "medical-certificate", th: "ใบรับรองแพทย์", en: "medical certificate" },
  { slug: "driving-license", th: "ใบขับขี่", en: "driving licence" },
  { slug: "will", th: "พินัยกรรม", en: "will" },
  { slug: "commercial-invoice", th: "Invoice การค้า", en: "commercial invoice" },
] as const;
